/**
 * PracticeModal - Modal pour pratiquer l'écriture des caractères/mots
 * Modes: Calligraphie (canvas) et Clavier
 * 
 * Version responsive: s'adapte à la largeur visuelle du mot thaï
 * Alignement précis basé sur les métriques réelles du texte
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PenTool, Keyboard, Volume2, X, Check, RotateCcw, Loader2, ChevronRight, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Calculate precise position for Thai ghost text in calligraphy grid
 * Uses real text metrics (actualBoundingBoxAscent/Descent) for accurate baseline alignment
 * 
 * @param {string} text - Thai text to measure
 * @param {number} fontSize - Font size in pixels
 * @param {number} canvasWidth - Grid canvas width
 * @param {number} canvasHeight - Grid canvas height
 * @param {number} baselineRatio - Position of baseline line (0.65 = 65% from top)
 * @param {number} horizontalPadding - Minimum horizontal padding
 * @returns {Object} Position and metrics for text placement
 */
const calculateThaiGhostPosition = (text, fontSize, canvasWidth, canvasHeight, baselineRatio = 0.65, horizontalPadding = 24) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const fontFamily = 'Sarabun, sans-serif';
  const fontWeight = 500;
  
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  const metrics = ctx.measureText(text);
  
  // Get real text metrics
  // actualBoundingBoxAscent: distance from baseline to top of text
  // actualBoundingBoxDescent: distance from baseline to bottom of text
  const hasAdvancedMetrics = 
    typeof metrics.actualBoundingBoxAscent === 'number' && 
    typeof metrics.actualBoundingBoxDescent === 'number';
  
  let ascent, descent, textWidth;
  
  if (hasAdvancedMetrics) {
    ascent = metrics.actualBoundingBoxAscent;
    descent = metrics.actualBoundingBoxDescent;
    textWidth = metrics.width;
  } else {
    // Fallback: estimate based on typical Thai font proportions
    // Thai fonts typically have ascent ~80% and descent ~20% of font size
    ascent = fontSize * 0.80;
    descent = fontSize * 0.20;
    textWidth = metrics.width;
  }
  
  const totalHeight = ascent + descent;
  
  // Calculate the Y position where baseline should be placed
  // The orange line is at baselineRatio (65%) from the top
  const baselineY = canvasHeight * baselineRatio;
  
  // To position text so its baseline aligns with baselineY:
  // The top of the text = baselineY - ascent
  const textTop = baselineY - ascent;
  
  // Calculate horizontal centering
  const availableWidth = canvasWidth - (horizontalPadding * 2);
  let adjustedFontSize = fontSize;
  let adjustedTextWidth = textWidth;
  let adjustedAscent = ascent;
  let adjustedDescent = descent;
  
  // If text is too wide, reduce font size proportionally
  if (textWidth > availableWidth) {
    const scaleFactor = availableWidth / textWidth;
    adjustedFontSize = Math.floor(fontSize * scaleFactor);
    
    // Recalculate metrics with adjusted font size
    ctx.font = `${fontWeight} ${adjustedFontSize}px ${fontFamily}`;
    const adjustedMetrics = ctx.measureText(text);
    
    if (hasAdvancedMetrics) {
      adjustedAscent = adjustedMetrics.actualBoundingBoxAscent;
      adjustedDescent = adjustedMetrics.actualBoundingBoxDescent;
    } else {
      adjustedAscent = adjustedFontSize * 0.80;
      adjustedDescent = adjustedFontSize * 0.20;
    }
    adjustedTextWidth = adjustedMetrics.width;
  }
  
  // Recalculate Y position with adjusted metrics
  const adjustedTextTop = baselineY - adjustedAscent;
  
  // Center horizontally
  const textLeft = (canvasWidth - adjustedTextWidth) / 2;
  
  return {
    x: textLeft,
    y: adjustedTextTop,
    fontSize: adjustedFontSize,
    textWidth: adjustedTextWidth,
    ascent: adjustedAscent,
    descent: adjustedDescent,
    totalHeight: adjustedAscent + adjustedDescent,
    baselineY: baselineY,
    hasAdvancedMetrics,
    fontFamily,
    fontWeight,
  };
};

/**
 * Measure the visual width of Thai text using canvas
 * Returns width in pixels for a given font size
 */
const measureThaiTextWidth = (text, fontSize = 100) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = `500 ${fontSize}px Sarabun, sans-serif`;
  const metrics = ctx.measureText(text);
  return metrics.width;
};

/**
 * Determine size category based on visual width
 * Returns: 'short' | 'medium' | 'long' | 'very-long'
 */
const getTextSizeCategory = (text) => {
  if (!text) return 'short';
  
  // Measure at reference font size
  const width = measureThaiTextWidth(text, 100);
  
  // Thresholds based on visual width at 100px font
  // Short: single char or vowel cluster (< 120px)
  // Medium: 2-3 syllables (120-280px)
  // Long: 4-5 syllables (280-450px)
  // Very long: 6+ syllables (> 450px)
  
  if (width < 120) return 'short';
  if (width < 280) return 'medium';
  if (width < 450) return 'long';
  return 'very-long';
};

/**
 * Get responsive dimensions based on text category and screen size
 */
const getResponsiveDimensions = (category, windowWidth) => {
  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  
  // Base configurations per category
  const configs = {
    'short': {
      modalClass: 'sm:max-w-md',
      canvasWidth: isMobile ? 280 : 320,
      canvasHeight: 320,
      fontSize: isMobile ? 100 : 140,
      padding: 24,
    },
    'medium': {
      modalClass: 'sm:max-w-xl md:max-w-2xl',
      canvasWidth: isMobile ? Math.min(windowWidth - 48, 350) : isTablet ? 450 : 500,
      canvasHeight: 320,
      fontSize: isMobile ? 70 : isTablet ? 90 : 100,
      padding: 28,
    },
    'long': {
      modalClass: 'sm:max-w-2xl md:max-w-3xl lg:max-w-4xl',
      canvasWidth: isMobile ? Math.min(windowWidth - 48, 350) : isTablet ? 550 : 650,
      canvasHeight: 320,
      fontSize: isMobile ? 55 : isTablet ? 70 : 85,
      padding: 32,
    },
    'very-long': {
      modalClass: 'sm:max-w-3xl md:max-w-4xl lg:max-w-5xl',
      canvasWidth: isMobile ? Math.min(windowWidth - 48, 350) : isTablet ? 600 : 750,
      canvasHeight: 320,
      fontSize: isMobile ? 45 : isTablet ? 60 : 70,
      padding: 36,
    },
  };
  
  return configs[category] || configs['short'];
};

// Drawing Canvas Component with Enhanced Grid for Thai Writing
const DrawingCanvas = ({ character, dimensions }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  
  const { canvasWidth, canvasHeight, fontSize, padding } = dimensions;

  // Redraw when character or dimensions change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Update canvas dimensions
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    const ctx = canvas.getContext('2d');
    clearCanvas(ctx, canvas);
  }, [character, canvasWidth, canvasHeight]);

  const clearCanvas = useCallback((ctx, canvas) => {
    ctx.fillStyle = '#FDFBF7';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw background grid (subtle)
    ctx.strokeStyle = '#E2E8F0';
    ctx.lineWidth = 0.5;
    
    // Fine grid (25px)
    for (let x = 0; x <= canvas.width; x += 25) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y <= canvas.height; y += 25) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
    
    // Main writing zone guides (Thai script vertical zones)
    ctx.setLineDash([8, 4]);
    ctx.lineWidth = 1.5;
    
    // Zone for upper marks (sara above) - top 25%
    ctx.strokeStyle = '#93C5FD';
    ctx.beginPath();
    ctx.moveTo(0, canvas.height * 0.25);
    ctx.lineTo(canvas.width, canvas.height * 0.25);
    ctx.stroke();
    
    // Main baseline for consonants - at 65%
    ctx.strokeStyle = '#F97316';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, canvas.height * 0.65);
    ctx.lineTo(canvas.width, canvas.height * 0.65);
    ctx.stroke();
    
    // Zone for lower marks (sara below) - bottom guide at 85%
    ctx.strokeStyle = '#93C5FD';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, canvas.height * 0.85);
    ctx.lineTo(canvas.width, canvas.height * 0.85);
    ctx.stroke();
    
    // Vertical center guide
    ctx.strokeStyle = '#D97706';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(canvas.width / 2, 0);
    ctx.lineTo(canvas.width / 2, canvas.height);
    ctx.stroke();
    
    ctx.setLineDash([]);
    setHasDrawn(false);
  }, []);

  const getCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }, []);

  const startDrawing = useCallback((e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = '#1E293B';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setIsDrawing(true);
    setHasDrawn(true);
  }, [getCoordinates]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing, getCoordinates]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    clearCanvas(ctx, canvas);
  }, [clearCanvas]);

  return {
    canvasRef,
    hasDrawn,
    handleClear,
    startDrawing,
    draw,
    stopDrawing,
    fontSize,
    padding,
    canvasWidth,
    canvasHeight,
  };
};

// Keyboard Practice Component
const KeyboardPractice = ({ character, onComplete }) => {
  const [input, setInput] = useState('');
  const [attempts, setAttempts] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    setInput('');
    setAttempts(0);
    inputRef.current?.focus();
  }, [character]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setAttempts(prev => prev + 1);
    
    if (input === character) {
      onComplete(true);
      setInput('');
    } else {
      toast.error("Pas tout à fait... Réessayez !");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="w-full text-center text-4xl py-6 border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-teal-500 transition-colors"
          style={{fontFamily: 'Sarabun, sans-serif'}}
          placeholder="Tapez ici..."
          autoComplete="off"
          data-testid="keyboard-input"
        />
        {attempts > 0 && input !== character && (
          <p className="absolute -bottom-6 left-0 right-0 text-center text-sm text-red-500">
            Tentatives: {attempts}
          </p>
        )}
      </div>
      <Button
        type="submit"
        disabled={!input}
        className="w-full rounded-full bg-teal-500 hover:bg-teal-600"
        data-testid="validate-keyboard-btn"
      >
        <Check className="w-4 h-4 mr-2" />
        Valider
      </Button>
    </form>
  );
};

const PracticeModal = ({ isOpen, onClose, character, info, type, onSpeak, onComplete }) => {
  const [mode, setMode] = useState('calligraphy');
  const [sessionCount, setSessionCount] = useState(0);
  const [canvasKey, setCanvasKey] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Phrases en contexte (mots uniquement)
  const isWord = type === 'word';
  const [phase, setPhase] = useState('practice');
  const [ctx, setCtx] = useState(null);
  const [ctxStatus, setCtxStatus] = useState('idle');
  const [senseIdx, setSenseIdx] = useState(0);
  const [sentIdx, setSentIdx] = useState(0);
  const [segIdx, setSegIdx] = useState(0);
  const [showFrench, setShowFrench] = useState(false);
  const ctxRequestId = useRef(0);
  const ctxStatusRef = useRef('idle');
  ctxStatusRef.current = ctxStatus;
  const errorHandled = useRef(false);

  // Track window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset session count when character changes
  useEffect(() => {
    setSessionCount(0);
    setCanvasKey(0);
    setPhase('practice');
    setCtx(null);
    setCtxStatus('idle');
    setSenseIdx(0);
    setSentIdx(0);
    setSegIdx(0);
    setShowFrench(false);
    ctxRequestId.current += 1;
    errorHandled.current = false;
  }, [character]);

  const ctxValid = ctx && ctx.word === character;

  // Transitions de la phase loading pilotées par l'état
  useEffect(() => {
    if (phase !== 'loading') return;
    if (ctxStatus === 'ready' && ctxValid) {
      setSegIdx(0);
      setCanvasKey(prev => prev + 1);
      setPhase('writing');
    } else if (ctxStatus === 'error' && !errorHandled.current) {
      errorHandled.current = true;
      toast.error("Manee n'a pas pu préparer les phrases");
      setTimeout(() => onClose(), 1000);
    }
  }, [phase, ctxStatus, ctxValid]);

  const currentSentence = ctxValid ? ctx.senses[senseIdx]?.sentences[sentIdx] : null;
  const segments = currentSentence?.segments || [];
  const currentSegment = segments[segIdx] || '';
  const sentenceComplete = phase === 'writing' && segments.length > 0 && segIdx >= segments.length;
  const isLastSentenceOverall = ctxValid
    && senseIdx === ctx.senses.length - 1
    && sentIdx === ctx.senses[senseIdx].sentences.length - 1;
  const totalSentences = ctxValid ? ctx.senses.reduce((n, s) => n + s.sentences.length, 0) : 0;
  const sentenceNumber = ctxValid
    ? ctx.senses.slice(0, senseIdx).reduce((n, s) => n + s.sentences.length, 0) + sentIdx + 1
    : 0;

  // Calculate responsive dimensions based on character visual width
  const sizeCategory = useMemo(() => getTextSizeCategory(character), [character]);
  const dimensions = useMemo(() => getResponsiveDimensions(sizeCategory, windowWidth), [sizeCategory, windowWidth]);
  // Phase writing : largeur de modale fixée par phrase (segment le plus large), canvas par segment
  const widestSegment = useMemo(() => {
    if (!segments.length) return '';
    return segments.reduce((a, b) => (measureThaiTextWidth(b, 100) > measureThaiTextWidth(a, 100) ? b : a), segments[0]);
  }, [segments]);
  const sentenceDimensions = useMemo(
    () => getResponsiveDimensions(getTextSizeCategory(widestSegment), windowWidth),
    [widestSegment, windowWidth]
  );
  const segmentDimensions = useMemo(
    () => getResponsiveDimensions(getTextSizeCategory(currentSegment), windowWidth),
    [currentSegment, windowWidth]
  );

  const fetchContext = () => {
    const myId = ctxRequestId.current;
    setCtxStatus('pending');
    axios.post(`${API_URL}/api/practice/context-sentences`, {
      thai: character,
      french: info?.meaning || ''
    })
      .then((res) => {
        if (myId !== ctxRequestId.current) return;
        setCtx(res.data);
        setCtxStatus('ready');
      })
      .catch(() => {
        if (myId !== ctxRequestId.current) return;
        setCtxStatus('error');
      });
  };

  const handleComplete = async (success) => {
    await onComplete(character, type, mode, success);
    const newCount = sessionCount + 1;
    setSessionCount(newCount);
    setCanvasKey(prev => prev + 1);
    
    if (newCount >= 5) {
      if (isWord) {
        setPhase('loading');
        if (ctxStatusRef.current === 'idle' || ctxStatusRef.current === 'error') {
          fetchContext();
        }
        return;
      }
      toast.success(`Bravo ! 5 pratiques terminées pour "${character}" !`);
      setTimeout(() => {
        onClose();
      }, 1000);
    } else {
      toast.success(`Pratique ${newCount}/5 validée ! Encore ${5 - newCount} pour terminer.`);
      if (isWord && newCount === 1) {
        fetchContext();
      }
    }
  };

  const handleSegmentComplete = (success) => {
    if (!success) return;
    setCanvasKey(prev => prev + 1);
    const next = segIdx + 1;
    setSegIdx(next);
    if (next >= segments.length) {
      axios.patch(`${API_URL}/api/practice/context-sentences/${ctx.id}/written`, {
        sense_index: senseIdx,
        sentence_index: sentIdx
      }).catch((err) => console.warn('[context-sentences] written non enregistré', err));
    }
  };

  const handleNextSentence = () => {
    setCanvasKey(prev => prev + 1);
    setSegIdx(0);
    setShowFrench(false);
    if (sentIdx + 1 < ctx.senses[senseIdx].sentences.length) {
      setSentIdx(sentIdx + 1);
    } else if (senseIdx + 1 < ctx.senses.length) {
      setSenseIdx(senseIdx + 1);
      setSentIdx(0);
    } else {
      toast.success(`Terminé pour ${character}`);
      setTimeout(() => onClose(), 1000);
    }
  };

  const handleFinish = () => {
    if (sessionCount > 0) {
      toast.success(`Session terminée ! ${sessionCount} pratique${sessionCount > 1 ? 's' : ''} enregistrée${sessionCount > 1 ? 's' : ''}.`);
    }
    onClose();
  };

  if (!character) return null;

  // Dynamic modal class based on text width
  const activeModalClass = phase === 'writing' && ctxValid ? sentenceDimensions.modalClass : dimensions.modalClass;
  const modalClass = `${activeModalClass} w-[95vw] max-h-[90vh] overflow-y-auto bg-[#FDFBF7] border-none shadow-2xl`;

  if (phase === 'loading' || (phase === 'writing' && !ctxValid)) {
    return (
      <Dialog open={isOpen} onOpenChange={handleFinish}>
        <DialogContent className={modalClass}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-slate-800 text-center">
              Pratiquer l'écriture
            </DialogTitle>
            <DialogDescription className="text-center text-slate-500">
              5 pratiques terminées pour {character}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-10" data-testid="context-loading">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            <p className="text-slate-600">Manee prépare tes phrases…</p>
          </div>
          <Button
            onClick={handleFinish}
            variant="outline"
            className="w-full rounded-full mt-2"
            data-testid="finish-practice-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Terminer la session
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  if (phase === 'writing') {
    return (
      <Dialog open={isOpen} onOpenChange={handleFinish}>
        <DialogContent className={modalClass}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-slate-800 text-center">
              Manee te propose des phrases avec {character}
            </DialogTitle>
            <DialogDescription className="text-center text-slate-500" data-testid="context-sense-label">
              {ctx.senses[senseIdx].label_fr}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center">
            <Badge className="bg-teal-500 text-white px-4 py-1" data-testid="context-progress-badge">
              mot {Math.min(segIdx + 1, segments.length)}/{segments.length} · phrase {sentenceNumber}/{totalSentences}
            </Badge>
          </div>

          {/* Phrase en construction */}
          <div
            className="flex flex-wrap justify-center gap-x-2 gap-y-1 py-4 px-2 bg-gradient-to-b from-amber-50 to-transparent rounded-2xl"
            style={{ fontFamily: 'Sarabun, sans-serif', fontWeight: 600, fontSize: '32px', lineHeight: 1.6 }}
            data-testid="context-sentence"
          >
            {segments.map((seg, i) => (
              <span
                key={i}
                className={
                  i < segIdx
                    ? 'text-slate-800'
                    : i === segIdx
                      ? 'bg-amber-200 text-slate-900 rounded px-1'
                      : 'text-slate-300'
                }
                data-testid={`context-segment-${i}`}
              >
                {seg}
              </span>
            ))}
          </div>

          {/* Traduction cachée par défaut */}
          <div className="flex flex-col items-center gap-1">
            <button
              type="button"
              onClick={() => setShowFrench(v => !v)}
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="toggle-french-btn"
            >
              {showFrench ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showFrench ? 'Masquer' : 'Voir la traduction'}
            </button>
            {showFrench && (
              <p className="text-sm text-slate-500 italic text-center" data-testid="context-french">
                {currentSentence.french}
              </p>
            )}
          </div>

          {sentenceComplete ? (
            <Button
              onClick={handleNextSentence}
              className="w-full rounded-full bg-teal-500 hover:bg-teal-600"
              data-testid="next-sentence-btn"
            >
              {isLastSentenceOverall ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Terminé pour {character}
                </>
              ) : (
                <>
                  <ChevronRight className="w-4 h-4 mr-2" />
                  Phrase suivante
                </>
              )}
            </Button>
          ) : (
            <Tabs value={mode} onValueChange={setMode} className="mt-2">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="calligraphy" data-testid="tab-calligraphy">
                  <PenTool className="w-4 h-4 mr-2" />
                  Calligraphie
                </TabsTrigger>
                <TabsTrigger value="keyboard" data-testid="tab-keyboard">
                  <Keyboard className="w-4 h-4 mr-2" />
                  Clavier
                </TabsTrigger>
              </TabsList>

              <TabsContent value="calligraphy" className="mt-4">
                <CalligraphyTab
                  key={canvasKey}
                  character={currentSegment}
                  dimensions={segmentDimensions}
                  onComplete={(hasDrawn) => handleSegmentComplete(hasDrawn)}
                />
              </TabsContent>

              <TabsContent value="keyboard" className="mt-4">
                <KeyboardPractice
                  key={canvasKey}
                  character={currentSegment}
                  onComplete={(success) => handleSegmentComplete(success)}
                />
              </TabsContent>
            </Tabs>
          )}

          <Button
            onClick={handleFinish}
            variant="outline"
            className="w-full rounded-full mt-2"
            data-testid="finish-practice-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Terminer la session
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleFinish}>
      <DialogContent className={modalClass}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-slate-800 text-center">
            Pratiquer l'écriture
          </DialogTitle>
          <DialogDescription className="text-center text-slate-500">
            Reproduisez le caractère ci-dessous (plusieurs fois si vous voulez)
          </DialogDescription>
        </DialogHeader>

        {/* Session counter */}
        {sessionCount > 0 && (
          <div className="flex justify-center">
            <Badge className={`${sessionCount >= 5 ? 'bg-green-500' : 'bg-teal-500'} text-white px-4 py-1`}>
              {sessionCount}/5 pratique{sessionCount > 1 ? 's' : ''}
            </Badge>
          </div>
        )}

        {/* Character display - responsive size */}
        <div className="text-center py-4 bg-gradient-to-b from-amber-50 to-transparent rounded-2xl">
          <span 
            className="text-slate-800 block"
            style={{
              fontFamily: 'Sarabun, sans-serif', 
              fontWeight: 600,
              fontSize: `${Math.max(dimensions.fontSize * 0.6, 48)}px`,
              lineHeight: 1.2,
            }}
            data-testid="practice-character"
          >
            {character}
          </span>
          {info && (
            <div className="mt-4 space-y-1">
              {info.name && <p className="text-lg text-slate-600" style={{fontFamily: 'Sarabun, sans-serif'}}>{info.name}</p>}
              {info.meaning && <p className="text-sm text-slate-500">{info.meaning}</p>}
              {info.sound && <p className="text-sm text-violet-600 font-medium">Son: {info.sound}</p>}
              {info.class && (
                <Badge variant="outline" className="mt-2">
                  Classe: {info.class}
                </Badge>
              )}
            </div>
          )}
          <button
            onClick={() => onSpeak(character)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-colors"
            data-testid="practice-speak-btn"
          >
            <Volume2 className="w-4 h-4" />
            Écouter
          </button>
        </div>

        {/* Mode tabs */}
        <Tabs value={mode} onValueChange={setMode} className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="calligraphy" data-testid="tab-calligraphy">
              <PenTool className="w-4 h-4 mr-2" />
              Calligraphie
            </TabsTrigger>
            <TabsTrigger value="keyboard" data-testid="tab-keyboard">
              <Keyboard className="w-4 h-4 mr-2" />
              Clavier
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="calligraphy" className="mt-4">
            <CalligraphyTab 
              key={canvasKey}
              character={character}
              dimensions={dimensions}
              onComplete={(hasDrawn) => handleComplete(hasDrawn)}
            />
          </TabsContent>
          
          <TabsContent value="keyboard" className="mt-4">
            <KeyboardPractice
              key={canvasKey}
              character={character}
              onComplete={(success) => handleComplete(success)}
            />
          </TabsContent>
        </Tabs>

        {/* Finish button */}
        <Button
          onClick={handleFinish}
          variant="outline"
          className="w-full rounded-full mt-2"
          data-testid="finish-practice-btn"
        >
          <X className="w-4 h-4 mr-2" />
          Terminer la session
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Separate component for calligraphy to use the hook properly
const CalligraphyTab = ({ character, dimensions, onComplete }) => {
  const {
    canvasRef,
    hasDrawn,
    handleClear,
    startDrawing,
    draw,
    stopDrawing,
    fontSize,
    padding,
    canvasWidth,
    canvasHeight,
  } = DrawingCanvas({ character, dimensions });

  // Calculate precise ghost text position using real text metrics
  const ghostPosition = useMemo(() => {
    return calculateThaiGhostPosition(
      character,
      fontSize,
      canvasWidth,
      canvasHeight,
      0.65, // baseline at 65%
      padding
    );
  }, [character, fontSize, canvasWidth, canvasHeight, padding]);

  return (
    <div className="space-y-4">
      <div className="relative flex justify-center">
        {/* Zone legend */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[10px] text-slate-400 pointer-events-none" style={{width: '20px'}}>
          <span className="text-blue-400">↑</span>
          <span className="text-orange-500 font-medium">—</span>
          <span className="text-blue-400">↓</span>
        </div>
        
        {/* Canvas container with responsive width */}
        <div className="relative ml-6" style={{ width: canvasWidth, height: canvasHeight }}>
          <canvas
            ref={canvasRef}
            width={canvasWidth}
            height={canvasHeight}
            className="border-2 border-slate-200 rounded-2xl cursor-crosshair touch-none"
            style={{ width: '100%', height: '100%' }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          
          {/* Character watermark - precisely aligned using real text metrics */}
          <div 
            className="absolute inset-0 pointer-events-none overflow-hidden"
          >
            <span 
              className="text-slate-300 opacity-20 whitespace-nowrap absolute"
              style={{
                fontFamily: ghostPosition.fontFamily, 
                fontWeight: ghostPosition.fontWeight,
                fontSize: `${ghostPosition.fontSize}px`,
                lineHeight: 1,
                // Position using calculated metrics
                // x centers the text, y positions top so baseline aligns with orange line
                left: `${ghostPosition.x}px`,
                top: `${ghostPosition.y}px`,
              }}
            >
              {character}
            </span>
          </div>
        </div>
      </div>
      
      {/* Writing zone legend */}
      <div className="flex justify-center gap-4 text-xs text-slate-500 flex-wrap">
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-300"></span> signes hauts</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-orange-500"></span> ligne de base</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-300"></span> signes bas</span>
      </div>
      
      <div className="flex gap-2 justify-center">
        <Button
          variant="outline"
          onClick={handleClear}
          className="rounded-full"
          data-testid="clear-canvas-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Effacer
        </Button>
        <Button
          onClick={() => onComplete(hasDrawn)}
          disabled={!hasDrawn}
          className="rounded-full bg-teal-500 hover:bg-teal-600"
          data-testid="validate-drawing-btn"
        >
          <Check className="w-4 h-4 mr-2" />
          Valider
        </Button>
      </div>
    </div>
  );
};

export default PracticeModal;
